import useTranslator from "../../hooks/useTranslator";
import {
  amlKycChecks,
  haveValue,
  ONFIDO_TOOLTIP_PRIORITY,
  getConsiderResult,
  getOnfidoCustomMessage,
} from "../../helper/common";
import CloseIcon from "../../assets/img/closeIcon.svg";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useContext, useMemo, useRef, useState } from "react";
import moreInfoimg from "../../assets/img/moreInfoimg.svg";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";
import LandLordContext from "../../context/landlord/LandLordContext";
import DownloadIcon from "../../assets/img/downloadIcon.svg";
import LoadingSpinner from "../LoadingSpinner";

const KycAmlCheckReportModal = ({ isOpen, onClose, user }) => {
  const { _t } = useTranslator();
  const { getOnfidoTimelineReport } = useContext(LandLordContext);
  const kycAmlCheckResult = amlKycChecks(user);

  const [showDropdown, setShowDropDown] = useState(false);
  const output = user?.onfido_user_kyc?.onfido_workflow_res?.resource.output;

  const mediaLinksRef = useRef();
  const pepLinksRef = useRef();
  const sanctionLinksRef = useRef();
  const warningLinksRef = useRef();
  const adverseMediaLinksRef = useRef();

  const [isOpenMediaLinks, setIsOpenMediaLinks] = useState(false);
  const [isOpenPepLinks, setIsOpenPepLinks] = useState(false);
  const [isOpenSanctionLinks, setIsOpenSanctionLinks] = useState(false);
  const [isOpenWarningLinks, setIsOpenWarningLinks] = useState(false);
  const [isOpenAdverseMediaLinks, setIsOpenAdverseMediaLinks] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isSanctionsCase =
    output?.document_report?.breakdown?.image_integrity?.breakdown
      ?.supported_document?.properties?.issuing_country_subject_to_sanctions ===
    "consider";

  const getStatusStyle = (status) => {
    if (!status) return STATUS_STYLES.grey;

    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === "clear") {
      return STATUS_STYLES.green;
    }

    if (normalizedStatus === "failed") {
      return STATUS_STYLES.red;
    }

    return STATUS_STYLES.yellow;
  };

  const STATUS_STYLES = {
    yellow: {
      backgroundColor: "#FFFBEC",
      color: "#E1BB20",
      border: "1px solid #F2E09B",
    },
    green: {
      backgroundColor: "#F2FBE9",
      color: "#82CF3F",
      border: "1px solid #DAECC8",
    },
    red: {
      backgroundColor: "#FFE2E2",
      color: "#FF3C3C",
      border: "1px solid #EB9B9C",
    },
    grey: {
      backgroundColor: "#F7F7F7",
      color: "#253837",
      border: "1px solid #E6E6E6",
    },
  };

  const getDocumentReportTooltip = (type, breakdown) => {
    if (!breakdown) return "";

    // ── 1. Try priority list first ──────────────────────────────────────────
    const sectionEntries = ONFIDO_TOOLTIP_PRIORITY.filter((entry) =>
      entry.path.startsWith(`${type}.`)
    );

    for (const entry of sectionEntries) {
      const subKey = entry.path.split(".")[1];
      const subBreakdown = breakdown?.breakdown?.[subKey];

      if (subBreakdown?.result === "consider") {
        return entry?.explanation;
      }
    }

    // ── 2. Fallback to existing switch case ─────────────────────────────────
    switch (type) {
      case "visual_authenticity": {
        const b = breakdown?.breakdown;

        if (b?.original_document_present?.properties?.scan === "consider") {
          return "onfidoScanDocumentMessage";
        }
        if (
          b?.original_document_present?.properties?.photo_of_screen ===
          "consider"
        ) {
          return "onfidoPhotoOfScreenMessage";
        }
        if (b?.image_integrity?.properties?.blur === "consider") {
          return "na";
        }
        if (b?.document_present?.result === "consider") {
          return "na";
        }
        break;
      }

      case "data_validation": {
        const b = breakdown?.breakdown;

        if (
          b?.document_expiration?.result === "consider" ||
          b?.expiry_date?.result === "consider"
        ) {
          return "onfidoDocumentExpiryMessage";
        }
        break;
      }

      case "image_integrity": {
        const b = breakdown?.breakdown;

        if (
          b?.image_quality?.properties?.two_documents_uploaded === "consider"
        ) {
          return "onfidoTwoDocumentUploadedMessage";
        }
        if (
          b?.conclusive_document_quality?.properties?.missing_back ===
          "consider"
        ) {
          return "onfidoMissingBackMessage";
        }
        if (b?.image_quality?.properties?.other_photo_issue === "consider") {
          return "onfidoOtherPhotoIssueMessage";
        }
        if (
          b?.conclusive_document_quality?.properties
            ?.abnormal_document_features === "consider"
        ) {
          return "onfidoAbnormalDocumentFeaturesMessage";
        }
        break;
      }

      default:
        return "";
    }
    return "";
  };

  // ── Original media array (article media items from match.media[]) ──────────
  const mediaArray = useMemo(() => {
    const matches = output?.aml_report?.properties?.matches ?? [];
    const result = [];

    for (const match of matches) {
      if (!Array.isArray(match?.media)) continue;

      for (const item of match.media) {
        if (item?.url && item?.snippet && item.title !== "(no title)") {
          result.push({
            url: item.url,
            snippet: item.snippet,
            title: item.title,
          });
          if (result.length === 3) return result;
        }
      }
    }

    return result;
  }, [output]);

  // ── Helper: extract source links filtered by aml_type keyword prefix ───────
  const getSourceLinksByType = (typeKeyword) => {
    const matches = output?.aml_report?.properties?.matches ?? [];
    const result = [];

    for (const match of matches) {
      const sources = match?.sources ?? {};

      for (const [, source] of Object.entries(sources)) {
        const amlTypes = source?.aml_types ?? [];
        const matchesType = amlTypes.some((t) =>
          t.toLowerCase().startsWith(typeKeyword.toLowerCase())
        );

        if (matchesType && source?.url) {
          const alreadyAdded = result.some((r) => r.url === source.url);
          if (!alreadyAdded) {
            result.push({
              url: source.url,
              title: source.name || source.url,
            });
          }
          if (result.length === 3) return result;
        }
      }
    }

    return result;
  };

  const pepLinks = useMemo(() => getSourceLinksByType("pep"), [output]);
  const sanctionLinks = useMemo(
    () => getSourceLinksByType("sanction"),
    [output]
  );
  const warningLinks = useMemo(() => getSourceLinksByType("warning"), [output]);
  const adverseMediaLinks = useMemo(
    () => getSourceLinksByType("adverse-media"),
    [output]
  );

  // ── Scroll helpers ─────────────────────────────────────────────────────────
  const moveToMediaLinks = () => {
    setIsOpenMediaLinks(true);
    setTimeout(() => {
      mediaLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToPepLinks = () => {
    setIsOpenPepLinks(true);
    setTimeout(() => {
      pepLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToSanctionLinks = () => {
    setIsOpenSanctionLinks(true);
    setTimeout(() => {
      sanctionLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToWarningLinks = () => {
    setIsOpenWarningLinks(true);
    setTimeout(() => {
      warningLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const moveToAdverseMediaLinks = () => {
    setIsOpenAdverseMediaLinks(true);
    setTimeout(() => {
      adverseMediaLinksRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 20);
  };

  const cleanSnippet = (str) => {
    if (!str) return "";

    let cleaned = str.trim();

    // Remove leading : "
    cleaned = cleaned.replace(/^:\s*"/, "");

    // Remove wrapping quotes
    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
      cleaned = cleaned.slice(1, -1);
    }

    // Fix double-escaped unicode (\\u → \u)
    cleaned = cleaned.replace(/\\\\u/g, "\\u");

    // Decode unicode characters
    cleaned = cleaned.replace(/\\u([\dA-Fa-f]{4})/g, (_, code) =>
      String.fromCharCode(parseInt(code, 16))
    );

    return cleaned;
  };

  const getDomain = (url) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch (error) {
      return "";
    }
  };

  const toggleDropdown = () => {
    if (isSanctionsCase) {
      return;
    }
    setShowDropDown(!showDropdown);
  };

  const onfidoMessage = getOnfidoCustomMessage(user, _t);

  const handleDownloadReport = async () => {
    try {
      setIsDownloading(true);
      const res = await getOnfidoTimelineReport(user?._id);
      if (res?.status === "success" && res?.file_name) {
        const url = `${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${res.file_name}`;
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Reusable link section renderer ────────────────────────────────────────
  const renderLinkSection = ({ links, isOpen, setIsOpen, ref, label }) => {
    if (!links.length) return null;

    return (
      <div className="media-links-wrapper" ref={ref}>
        <div
          className="d-flex w-100 cursor-pointer align-items-center link-heading-wrapper justify-content-between"
          onClick={() => setIsOpen(!isOpen)}
        >
          <h5 className="d-flex align-items-center justify-content-start custom-gap-2">
            <img
              src="/images/link-icon-grey.svg"
              alt="Link"
              className="link-img"
            />
            {label}
            <p className="docnumber">{links.length}</p>
          </h5>
          <img
            src="/images/arrow-right.svg"
            alt="Arrow"
            className="arrow-down-icon"
            style={{ rotate: isOpen ? "268deg" : "90deg" }}
          />
        </div>
        {isOpen &&
          links.map((link, index) => (
            <div className="link-main-wrapper" key={index}>
              {haveValue(link?.url) ? (
                <div className="d-flex align-items-center link-wrapper">
                  <p className="docnumber">{index + 1}</p>
                  <a
                    className="underline"
                    href={link?.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="one-line-clamp">{link?.title}</span>
                    <span className="d-flex align-items-center custom-gap-1">
                      {getDomain(link?.url)}
                    </span>
                    <img
                      src="/images/external-link.svg"
                      alt="Link"
                      className="link-img"
                    />
                  </a>
                </div>
              ) : (
                <span>{_t("urlNotFound")}</span>
              )}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="wrapper-modal" onClick={onClose}>
      <div
        className={`doc-history-popup total-salary-modal default-check-modal ${isOpen ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="doc-history-header">
          <img
            src={CloseIcon}
            alt=""
            className="doc-history-close-btn"
            onClick={onClose}
          />
          <h4 className="custom-modal-heading">
            {_t("kycAndAmlVerificationCheck")}
          </h4>
        </div>
        <div
          class="risk-card"
          style={{
            borderColor: kycAmlCheckResult ? "#c1f7d6" : "#fbc2c2",
            backgroundColor: kycAmlCheckResult ? "#edfdf3" : "#fef2f1",
          }}
        >
          <div class="risk-card-top">
            <div class="risk-card-left">
              <div class="risk-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={kycAmlCheckResult ? "#15a24a" : "#ef4343"}
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-circle-check h-6 w-6 text-risk-safe"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>{" "}
                <span>{_t("kycAndAmlStatus")}</span>
              </div>
              <div
                style={{
                  color: kycAmlCheckResult ? "#15a24a" : "#ef4343",
                }}
                class="risk-card-value"
              >
                {isSanctionsCase
                  ? _t("unsupportedDocumentCountry")
                  : kycAmlCheckResult
                    ? _t("passed")
                    : _t("notPassed")}
              </div>
            </div>
            <button
              className="kyc-aml-download-btn"
              onClick={handleDownloadReport}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <LoadingSpinner
                  size={"0.8233vw"}
                  color="#253837"
                  borderWidth={1}
                  className="docs-loading-spinner"
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="kyc-aml-download-btn-icon"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              {isDownloading ? `${_t("downloading")}...` : _t("downloadReport")}
            </button>
          </div>
          <>
            <div class="risk-card-bottom">
              <div>
                <div style={{ fontWeight: "500" }} className="risk-card-status">
                  {isSanctionsCase
                    ? _t("sanctionsCaseKycAndAmlCheckWarningMessage")
                    : _t(
                        kycAmlCheckResult
                          ? "successDescriptionInsideModelForKycAndAmlAnalysisCollectively"
                          : "failureDescriptionInsideModelForKycAndAmlAnalysisCollectively"
                      )}
                </div>
                {(onfidoMessage?.explanation ||
                  onfidoMessage?.recommendedActions) && (
                  <div className="risk-card-status risk-card-status-onfido-message">
                    <div style={{ fontWeight: "500" }}>
                      <span style={{ fontWeight: "bold" }}>
                        {_t("explanationSummary")}:{" "}
                      </span>
                      {onfidoMessage?.explanation}
                    </div>
                    <div style={{ fontWeight: "500" }}>
                      <span style={{ fontWeight: "bold" }}>
                        {_t("recommendedActions")}:{" "}
                      </span>
                      {onfidoMessage?.recommended_action}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        </div>

        <div
          className="detail-debt-info-container"
          style={{
            opacity: isSanctionsCase ? 0.3 : 1,
            background: isSanctionsCase ? "rgba(255, 255, 255, 0.5)" : "",
          }}
        >
          <div
            className={`detail-debt-info-container-header ${!isSanctionsCase ? "cursor-pointer" : ""}`}
            onClick={toggleDropdown}
          >
            <p>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-user-check w-5 h-5 text-muted-foreground"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <polyline points="16 11 18 13 22 9"></polyline>
              </svg>
              <span>{_t("detailedKycAmlInformation")}</span>
            </p>
            {showDropdown ? (
              <IoIosArrowUp
                onClick={toggleDropdown}
                size={18}
                style={{ color: "#253837" }}
              />
            ) : (
              <IoIosArrowDown
                onClick={toggleDropdown}
                data-tooltip-id={isSanctionsCase ? "button-action-tooltip" : ""}
                size={18}
                style={{ color: "#253837" }}
              />
            )}
          </div>

          {showDropdown && (
            <div className="detail-debt-info-container-dropdown">
              {/* ── Identity Details ── */}
              <div className="detail-debt-info-container-dropdown-info">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-id-card w-4 h-4 text-muted-foreground"
                  >
                    <path d="M16 10h2"></path>
                    <path d="M16 14h2"></path>
                    <path d="M6.17 15a3 3 0 0 1 5.66 0"></path>
                    <circle cx="9" cy="11" r="2"></circle>
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                  </svg>
                  <span>{_t("identityDetails")}</span>
                </div>
                <div className="detail-debt-info-container-dropdown-info-data grid-two">
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("firstName")}</h2>
                    <span>{output?.first_name || _t("notAvailable")}</span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("lastName")}</h2>
                    <span>{output?.last_name || _t("notAvailable")}</span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("dateOfBirth")}</h2>
                    <span>
                      {haveValue(output?.date_of_birth)
                        ? moment(output?.date_of_birth).format("DD/MM/YYYY")
                        : _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("nationality")}</h2>
                    <span>{_t(output?.nationality) || _t("notAvailable")}</span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("gender")}</h2>
                    <span>
                      {output?.document_report?.properties?.gender ||
                        _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("placeOfBirth")}</h2>
                    <span>
                      {output?.document_report?.properties?.place_of_birth ||
                        _t("notAvailable")}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Document Details ── */}
              <div className="detail-debt-info-container-dropdown-info">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-file-text w-4 h-4 text-muted-foreground"
                  >
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    <path d="M10 9H8"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                  </svg>
                  <span>{_t("documentDetails")}</span>
                </div>
                <div className="detail-debt-info-container-dropdown-info-data grid-two">
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("documentTypeOnfido")}</h2>
                    <span>
                      {_t(output?.document_report?.properties?.document_type) ||
                        _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("documentNumber")}</h2>
                    <span>
                      {output?.document_report?.properties?.document_number ||
                        _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("issuingDate")}</h2>
                    <span>
                      {haveValue(
                        output?.document_report?.properties?.issuing_date
                      )
                        ? moment(
                            output?.document_report?.properties?.issuing_date
                          ).format("DD/MM/YYYY")
                        : _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("expiryDate")}</h2>
                    <span>
                      {haveValue(
                        output?.document_report?.properties?.date_of_expiry
                      )
                        ? moment(
                            output?.document_report?.properties?.date_of_expiry
                          ).format("DD/MM/YYYY")
                        : _t("notAvailable")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values">
                    <h2>{_t("issuingCountry")}</h2>
                    <span>
                      {output?.document_report?.properties?.issuing_country ||
                        _t("notAvailable")}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Document Checks ── */}
              <div className="detail-debt-info-container-dropdown-info">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-square-check-big w-4 h-4 text-muted-foreground"
                  >
                    <path d="M21 10.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12.5"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  <span>{_t("documentChecks")}</span>
                  <img
                    data-tooltip-id="document-checks-tooltip"
                    src={moreInfoimg}
                    alt="Vector Icon"
                    className="vactorImg vactorImg1 default-check-img"
                  />
                  <ReactTooltip
                    id="document-checks-tooltip"
                    noArrow
                    className="react-tooltip-styling details-skor-tooltip"
                    render={() => (
                      <p>
                        {_t(
                          "documentChecksDescriptionInsideCollectiveKycAndAmlChecks"
                        )}
                      </p>
                    )}
                  />
                </div>
                <div className="detail-debt-info-container-dropdown-info-data remove-last-border">
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("dataConsistency")}</h2>
                    <span
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.data_consistency
                          ?.result
                      )}
                    >
                      {_t(
                        output?.document_report?.breakdown?.data_consistency
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("visualAuthenticity")}</h2>
                    <ReactTooltip
                      id="visual-authenticity-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => {
                        const tooltipData = getDocumentReportTooltip(
                          "visual_authenticity",
                          output?.document_report?.breakdown
                            ?.visual_authenticity
                        );
                        if (!tooltipData) return;
                        return <p>{_t(tooltipData)}</p>;
                      }}
                    />
                    <span
                      data-tooltip-id="visual-authenticity-warning"
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.visual_authenticity
                          ?.result
                      )}
                      className={`${
                        output?.document_report?.breakdown?.visual_authenticity
                          ?.result === "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.document_report?.breakdown?.visual_authenticity
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("ageValidation")}</h2>
                    <span
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.age_validation
                          ?.result
                      )}
                    >
                      {_t(
                        output?.document_report?.breakdown?.age_validation
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("imageIntegrity")}</h2>
                    <ReactTooltip
                      id="image-integrity-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => {
                        const tooltipData = getDocumentReportTooltip(
                          "image_integrity",
                          output?.document_report?.breakdown?.image_integrity
                        );
                        if (!tooltipData) return;
                        return <p>{_t(tooltipData)}</p>;
                      }}
                    />
                    <span
                      data-tooltip-id="image-integrity-warning"
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.image_integrity
                          ?.result
                      )}
                      className={`${
                        output?.document_report?.breakdown?.image_integrity
                          ?.result === "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.document_report?.breakdown?.image_integrity
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("dataValidation")}</h2>
                    <ReactTooltip
                      id="data-validation-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => {
                        const tooltipData = getDocumentReportTooltip(
                          "data_validation",
                          output?.document_report?.breakdown?.data_validation
                        );
                        if (!tooltipData) return;
                        return <p>{_t(tooltipData)}</p>;
                      }}
                    />
                    <span
                      data-tooltip-id="data-validation-warning"
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.data_validation
                          ?.result
                      )}
                      className={`${
                        output?.document_report?.breakdown?.data_validation
                          ?.result === "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.document_report?.breakdown?.data_validation
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("compromisedDocument")}</h2>
                    <span
                      style={getStatusStyle(
                        output?.document_report?.breakdown?.compromised_document
                          ?.result
                      )}
                    >
                      {_t(
                        output?.document_report?.breakdown?.compromised_document
                          ?.result
                      ) || _t("na")}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── AML Checks ── */}
              <div className="detail-debt-info-container-dropdown-info">
                <div className="d-flex align-items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-scale w-4 h-4 text-muted-foreground"
                  >
                    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path>
                    <path d="M7 21h10"></path>
                    <path d="M12 3v18"></path>
                    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"></path>
                  </svg>
                  <span>{_t("amlChecks")}</span>
                  <img
                    data-tooltip-id="aml-checks-tooltip"
                    src={moreInfoimg}
                    alt="Vector Icon"
                    className="vactorImg vactorImg1 default-check-img"
                  />
                  <ReactTooltip
                    id="aml-checks-tooltip"
                    noArrow
                    className="react-tooltip-styling details-skor-tooltip"
                    render={() => (
                      <p>
                        {_t(
                          "amlChecksDescriptionInsideCollectiveKycAndAmlChecks"
                        )}
                      </p>
                    )}
                  />
                </div>
                <div className="detail-debt-info-container-dropdown-info-data remove-last-border">
                  {/* Sanctions row */}
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("sanctions")}</h2>
                    <ReactTooltip
                      clickable
                      id="sanctions-message-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => (
                        <div>
                          <p>{_t("onfidoSanctionsWarningMessage")}</p>
                          {sanctionLinks.length > 0 && (
                            <p
                              className="underline p-0 mt-2 cursor-pointer"
                              onClick={moveToSanctionLinks}
                            >
                              {_t("seeAvailableSources")}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <span
                      data-tooltip-id={`${
                        output?.aml_report?.breakdown?.sanction?.result ===
                        "consider"
                          ? "sanctions-message-warning"
                          : ""
                      }`}
                      style={getStatusStyle(
                        output?.aml_report?.breakdown?.sanction?.result
                      )}
                      className={`${
                        output?.aml_report?.breakdown?.sanction?.result ===
                        "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(output?.aml_report?.breakdown?.sanction?.result) ||
                        _t("na")}
                    </span>
                  </div>

                  {/* Politically Exposed Person row */}
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("politicallyExposedPerson")}</h2>
                    <ReactTooltip
                      clickable
                      id="pep-message-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => (
                        <div>
                          <p>{_t("onfidoPEPWarningMessage")}</p>
                          {pepLinks.length > 0 && (
                            <p
                              className="underline p-0 mt-2 cursor-pointer"
                              onClick={moveToPepLinks}
                            >
                              {_t("seeAvailableSources")}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <span
                      data-tooltip-id={`${
                        output?.aml_report?.breakdown
                          ?.politically_exposed_person?.result === "consider"
                          ? "pep-message-warning"
                          : ""
                      }`}
                      style={getStatusStyle(
                        output?.aml_report?.breakdown
                          ?.politically_exposed_person?.result
                      )}
                      className={`${
                        output?.aml_report?.breakdown
                          ?.politically_exposed_person?.result === "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.aml_report?.breakdown
                          ?.politically_exposed_person?.result
                      ) || _t("na")}
                    </span>
                  </div>

                  {/* Adverse Media row */}
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("adverseMedia")}</h2>
                    <ReactTooltip
                      clickable
                      id="adverse-media-message-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => (
                        <div>
                          <p>{_t("onfidoAdverseMediaWarningMessage")}</p>
                          {adverseMediaLinks.length > 0 && (
                            <p
                              className="underline p-0 mt-2 cursor-pointer"
                              onClick={moveToAdverseMediaLinks}
                            >
                              {_t("seeAvailableSources")}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <span
                      data-tooltip-id={`${
                        output?.aml_report?.breakdown?.adverse_media?.result ===
                        "consider"
                          ? "adverse-media-message-warning"
                          : ""
                      }`}
                      style={getStatusStyle(
                        output?.aml_report?.breakdown?.adverse_media?.result
                      )}
                      className={`${
                        output?.aml_report?.breakdown?.adverse_media?.result ===
                        "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.aml_report?.breakdown?.adverse_media?.result
                      ) || _t("na")}
                    </span>
                  </div>

                  {/* Legal & Regulatory Warnings row */}
                  <div className="detail-debt-info-container-dropdown-info-data-values border-with-tags">
                    <h2>{_t("legalAndRegulatoryWarnings")}</h2>
                    <ReactTooltip
                      clickable
                      id="legal-regulatory-message-warning"
                      noArrow
                      className="react-tooltip-styling details-skor-tooltip"
                      render={() => (
                        <div>
                          <p>{_t("onfidoLegalAndRegulatoryWarningMessage")}</p>
                          {warningLinks.length > 0 && (
                            <p
                              className="underline p-0 mt-2 cursor-pointer"
                              onClick={moveToWarningLinks}
                            >
                              {_t("seeAvailableSources")}
                            </p>
                          )}
                        </div>
                      )}
                    />
                    <span
                      data-tooltip-id={`${
                        output?.aml_report?.breakdown
                          ?.legal_and_regulatory_warnings?.result === "consider"
                          ? "legal-regulatory-message-warning"
                          : ""
                      }`}
                      style={getStatusStyle(
                        output?.aml_report?.breakdown
                          ?.legal_and_regulatory_warnings?.result
                      )}
                      className={`${
                        output?.aml_report?.breakdown
                          ?.legal_and_regulatory_warnings?.result === "consider"
                          ? "cursor-pointer"
                          : ""
                      }`}
                    >
                      {_t(
                        output?.aml_report?.breakdown
                          ?.legal_and_regulatory_warnings?.result
                      ) || _t("na")}
                    </span>
                  </div>

                  {/* ── PEP Links section ── */}
                  {renderLinkSection({
                    links: pepLinks,
                    isOpen: isOpenPepLinks,
                    setIsOpen: setIsOpenPepLinks,
                    ref: pepLinksRef,
                    label: _t("pepLinks"),
                  })}

                  {/* ── Sanction Links section ── */}
                  {renderLinkSection({
                    links: sanctionLinks,
                    isOpen: isOpenSanctionLinks,
                    setIsOpen: setIsOpenSanctionLinks,
                    ref: sanctionLinksRef,
                    label: _t("sanctionLinks"),
                  })}

                  {/* ── Legal & Regulatory Warning Links section ── */}
                  {renderLinkSection({
                    links: warningLinks,
                    isOpen: isOpenWarningLinks,
                    setIsOpen: setIsOpenWarningLinks,
                    ref: warningLinksRef,
                    label: _t("legalWarningLinks"),
                  })}

                  {/* ── Adverse Media Links section ── */}
                  {renderLinkSection({
                    links: adverseMediaLinks,
                    isOpen: isOpenAdverseMediaLinks,
                    setIsOpen: setIsOpenAdverseMediaLinks,
                    ref: adverseMediaLinksRef,
                    label: _t("adverseMediaLinks"),
                  })}

                  {/* ── Original Media Links section (unchanged) ── */}
                  <div className="media-links-wrapper" ref={mediaLinksRef}>
                    {mediaArray.length > 0 && (
                      <div
                        className="d-flex w-100 cursor-pointer align-items-center link-heading-wrapper justify-content-between"
                        onClick={() => setIsOpenMediaLinks(!isOpenMediaLinks)}
                      >
                        <h5 className="d-flex align-items-center justify-content-start custom-gap-2">
                          <img
                            src="/images/link-icon-grey.svg"
                            alt="Link"
                            className="link-img"
                          />
                          {_t("mediaLinks")}
                          <p className="docnumber">{mediaArray.length}</p>
                        </h5>
                        <img
                          src="/images/arrow-right.svg"
                          alt="Arrow"
                          className="arrow-down-icon"
                          style={{
                            rotate: isOpenMediaLinks ? "268deg" : "90deg",
                          }}
                        />
                      </div>
                    )}
                    {isOpenMediaLinks &&
                      mediaArray.map((media, index) => (
                        <div className="link-main-wrapper" key={index}>
                          {haveValue(media?.url) ? (
                            <div className="d-flex align-items-center link-wrapper">
                              <p className="docnumber">{index + 1}</p>
                              <a
                                className="underline"
                                href={media?.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="one-line-clamp">
                                  {media?.title}
                                </span>
                                <span className="d-flex align-items-center custom-gap-1">
                                  {getDomain(media?.url)}
                                </span>
                                <img
                                  src="/images/external-link.svg"
                                  alt="Link"
                                  className="link-img"
                                />
                              </a>
                            </div>
                          ) : (
                            <span>{_t("urlNotFound")}</span>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <ButtonActionTooltip />
      </div>
    </div>
  );
};

export default KycAmlCheckReportModal;
